import { useState, useEffect, useCallback } from 'react';
 
import { Profile, Filters } from '../types';
import { api } from '../services/api';

interface UseTutorsOptions {
  filters?: Filters;
  autoLoad?: boolean;
}

export const useTutors = (options: UseTutorsOptions = { autoLoad: true }) => {
  const [tutors, setTutors] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<string>('Connexion en cours...');

  const loadTutors = useCallback(async (filters?: Filters) => {
    try {
      setConnectionStatus('Récupération des tuteurs depuis Supabase...');
      
      const { data, error: supabaseError } = await api.profiles.getTutors(filters);
      
      if (supabaseError) {
        throw supabaseError;
      }

      if (data) {
        setTutors(data);
        setConnectionStatus(`${data.length} tuteur(s) chargé(s) depuis Supabase`);
        setError(null);
      } else {
        // Aucune donnée trouvée
        setTutors([]);
        setConnectionStatus('Aucun tuteur trouvé dans la base de données');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des tuteurs');
      
      // En cas d'erreur, ne pas afficher de données de fallback
      setTutors([]);
      setConnectionStatus('Erreur de connexion à la base de données');
    }
  }, []);

  const initializeConnection = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setConnectionStatus('Test de connexion à Supabase...');
      
      // Test de connexion d'abord
      const connectionTest = await api.testConnection();
      
      if (!connectionTest.success) {
        throw new Error(`Échec de connexion à Supabase: ${connectionTest.error}`);
      }
      
      setConnectionStatus('Connexion réussie, chargement des tuteurs...');
      await loadTutors(options.filters);
      
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'initialisation');
      setConnectionStatus('Connectez-vous pour accéder aux tuteurs');
      
      // En cas d'erreur, ne pas utiliser de données de fallback
      setTutors([]);
    } finally {
      setLoading(false);
    }
  }, [options.filters, loadTutors]);

  useEffect(() => {
    if (options.autoLoad) {
      initializeConnection();
    }
  }, [options.autoLoad, initializeConnection]);

  const refreshTutors = useCallback((filters?: Filters) => {
    loadTutors(filters);
  }, [loadTutors]);

  const searchTutors = useCallback(async (filters: Filters) => {
    setLoading(true);
    await loadTutors(filters);
    setLoading(false);
  }, [loadTutors]);

  return {
    tutors,
    loading,
    error,
    connectionStatus,
    refreshTutors,
    searchTutors
  };
};

 